import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #visibleSlides = 1;
  #resizeObserver = undefined;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this);
    this.#syncVisibleSlides();
    this.#updateSlides();
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#resizeObserver?.disconnect();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    const maxIndex = Math.max(slides.length - this.#visibleSlides, 0);

    if (!slides.length) {
      this.#current = 0;
    } else if (current > maxIndex) {
      this.#current = 0;
    } else if (current < 0) {
      this.#current = maxIndex;
    } else {
      this.#current = current;
    }

    this.#updateSlides();
  }

  #handleResize = () => {
    this.#syncVisibleSlides();
    this.current = this.#current;
  };

  #syncVisibleSlides() {
    const slides = this.refs.slides ?? [];
    const width = this.clientWidth;
    let visibleSlides = 4;

    if (width <= 450) {
      visibleSlides = 1;
    } else if (width <= 500) {
      visibleSlides = 2;
    } else if (width <= 700) {
      visibleSlides = 3;
    }

    this.#visibleSlides = Math.max(1, Math.min(visibleSlides, slides.length || 1));
    this.style.setProperty('--announcement-visible-slides', `${this.#visibleSlides}`);
  }

  #updateSlides() {
    const slides = this.refs.slides ?? [];

    this.style.setProperty('--announcement-current-slide', `${this.#current}`);

    slides.forEach((slide, index) => {
      const isVisible = index >= this.#current && index < this.#current + this.#visibleSlides;
      slide.setAttribute('aria-hidden', `${!isVisible}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
